import { Logo } from "@components/brand/Logo";
import { StoreBadges } from "@components/shared/StoreBadges";
import { LicenceLine } from "@components/shared/LicenceLine";
import { CONTACT, FLEX, FRA, SOCIAL_LINKS } from "@/lib/site";
import { useTranslations } from "@services/strings";
import Link from "next/link";
import { ReactNode } from "react";

const Column = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div>
    <h2 className="text-sm font-semibold text-white">{title}</h2>
    <ul className="mt-4 flex flex-col gap-2.5 text-[15px] text-slate-300">
      {children}
    </ul>
  </div>
);

const Item = ({
  href,
  children,
  external = false,
}: {
  href: string;
  children: ReactNode;
  external?: boolean;
}) => (
  <li>
    {external ? (
      <a
        href={href}
        target="_blank"
        rel="noopener"
        className="hover:text-white"
      >
        {children}
      </a>
    ) : (
      <Link href={href} className="hover:text-white">
        {children}
      </Link>
    )}
  </li>
);

/** Navy footer: products, company, support, store badges and the regulator line. */
export const SiteFooter = () => {
  const { t } = useTranslations();

  return (
    <footer className="bg-navy-deep text-slate-300" id="download">
      <div className="container py-14 md:py-20">
        <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="col-span-2 max-w-sm md:col-span-1">
            <Logo height={24} className="text-white" />
            <p className="mt-5 text-body text-slate-300">
              {t("site.footer.tagline")}
            </p>
            <StoreBadges placement="footer" className="mt-6" />
          </div>

          <Column title={t("site.footer.products")}>
            <Item href="/unlock">{t("site.nav.unlock")}</Item>
            <Item href="/pay-less">{t("site.nav.payLess")}</Item>
            <Item href="/mortgage">{t("site.nav.mortgage")}</Item>
            <Item href="/auto-loans">{t("site.nav.autoLoans")}</Item>
            {FLEX.live ? <Item href="/flex">{t("site.nav.flex")}</Item> : null}
          </Column>

          <Column title={t("site.footer.company")}>
            <Item href={CONTACT.careersUrl} external>
              {t("site.footer.careers")}
            </Item>
            <Item href="/contact">{t("site.nav.contact")}</Item>
            {FLEX.live ? (
              <Item href="/developers">{t("site.footer.developers")}</Item>
            ) : null}
          </Column>

          <Column title={t("site.footer.support")}>
            <Item href="/help">{t("site.nav.help")}</Item>
            <Item href="/static/privacy-policy">
              {t("site.footer.privacy")}
            </Item>
            <Item href="/static/cookie-policy">{t("site.footer.cookies")}</Item>
            <Item href="/static/delete-account">
              {t("site.footer.deleteAccount")}
            </Item>
            <Item href={FRA.clientProtection.guide} external>
              {t("site.footer.clientGuide")}
            </Item>
            <Item href={FRA.clientProtection.leaflet} external>
              {t("site.footer.clientLeaflet")}
            </Item>
          </Column>
        </div>

        <div className="mt-14 flex flex-col gap-6 border-t border-white/10 pt-8 text-sm text-slate-400 md:flex-row md:items-start md:justify-between">
          <div className="max-w-2xl">
            <LicenceLine tone="inverse" />
            <p className="mt-3">
              <a
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="hover:text-white"
              >
                {t("site.footer.registeredAddress")}
              </a>
            </p>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <div className="flex gap-5">
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener"
                className="hover:text-white"
              >
                Facebook
              </a>
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener"
                className="hover:text-white"
              >
                Instagram
              </a>
              <a
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noopener"
                className="hover:text-white"
              >
                LinkedIn
              </a>
            </div>
            {/* The year can differ between the build and the visit. */}
            <p suppressHydrationWarning>
              {t("site.footer.rights", { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
